import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { formatElapsed } from "@/lib/dateUtils";
import { toast } from "sonner";
import { Play, Square, Loader2 } from "lucide-react";
import ProjectPicker from "@/components/ProjectPicker";
import ActivityInput from "@/components/ActivityInput";
import { getLastProjectId, setLastProjectId } from "@/lib/projects";
import { clearRecentActivitiesCache } from "@/lib/recentActivities";

export default function ClockWidget({
  onEntryLogged,
  previewMode = false,
  previewTimer = null,
  onPreviewStart,
  onPreviewStop,
}) {
  const [timer, setTimer] = useState(null); // { activity, started_at, project_*}
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState("");
  const [projectId, setProjectId] = useState(getLastProjectId());
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [, setTick] = useState(0);

  // In preview mode the parent owns timer state; we mirror it locally so the
  // existing render branches stay unchanged.
  const activeTimer = previewMode ? previewTimer : timer;

  const loadTimer = async () => {
    try {
      const { data } = await api.get("/timer/active");
      setTimer(data || null);
    } catch (e) {
      setTimer(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (previewMode) {
      setLoading(false);
      return;
    }
    loadTimer();
  }, [previewMode]);

  // Re-render every second while timer is active to update elapsed display
  useEffect(() => {
    if (!activeTimer) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [activeTimer]);

  const handleStart = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = activity.trim();
    if (!trimmed) {
      setError("What are you working on?");
      return;
    }
    setSubmitting(true);
    try {
      if (previewMode) {
        await onPreviewStart?.({ activity: trimmed, project_id: projectId || null });
        setActivity("");
        setLastProjectId(projectId);
      } else {
        await api.post("/timer/start", {
          activity: trimmed,
          project_id: projectId || null,
        });
        await loadTimer();
        setActivity("");
        setLastProjectId(projectId);
        toast.success("Timer started");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start timer.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStop = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (previewMode) {
        await onPreviewStop?.();
      } else {
        const { data } = await api.post("/timer/stop");
        setTimer(null);
        // The newly-logged activity should appear in the typeahead next time
        // \u2014 drop the cache so the next focus refetches.
        clearRecentActivitiesCache();
        toast.success("Time logged", {
          description: `${data.hours}h \u00b7 ${data.activity}`,
        });
        onEntryLogged?.();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not stop timer.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 h-40 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={20} />
      </div>
    );
  }

  // ACTIVE TIMER
  if (activeTimer) {
    return (
      <div
        className="rounded-2xl border p-6 text-white relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          borderColor: BRAND.purple,
        }}
        data-testid="clock-widget-active"
      >
        <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-widest font-semibold">
          <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
          Timer running
        </div>
        <div
          className="mt-3 text-5xl font-mono font-bold tabular-nums"
          data-testid="timer-elapsed"
        >
          {formatElapsed(activeTimer.started_at)}
        </div>
        <div className="mt-2 text-sm text-white/90 break-words" data-testid="timer-activity">
          {activeTimer.activity}
        </div>
        {activeTimer.project_name && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20 text-white"
            data-testid="timer-project"
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: activeTimer.project_color || "#fff" }}
            />
            {activeTimer.project_name}
          </div>
        )}
        <button
          onClick={handleStop}
          disabled={submitting}
          data-testid="stop-timer-button"
          className="mt-5 inline-flex items-center gap-2 bg-white/95 hover:bg-white text-slate-900 font-semibold px-4 py-2 rounded-lg transition disabled:opacity-70"
        >
          {submitting ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <Square size={16} fill="currentColor" />
          )}
          Stop & Log
        </button>
        {error && (
          <div className="mt-3 text-sm bg-white/15 rounded-md px-3 py-2" data-testid="clock-error">
            {error}
          </div>
        )}
      </div>
    );
  }

  // IDLE
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-6"
      data-testid="clock-widget-idle"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
          Clock in
        </h3>
        <span className="text-xs text-slate-400">Live timer</span>
      </div>
      <form onSubmit={handleStart} className="mt-3 space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <ActivityInput
            value={activity}
            onChange={setActivity}
            placeholder="What are you working on?"
            disabled={submitting}
            maxLength={500}
            testId="timer-activity-input"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="start-timer-button"
            className="inline-flex items-center gap-2 justify-center text-white font-semibold px-5 py-2.5 rounded-lg transition disabled:opacity-70 shrink-0"
            style={{
              background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
            }}
          >
            {submitting ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <Play size={16} fill="currentColor" />
            )}
            Start
          </button>
        </div>
        <ProjectPicker
          value={projectId}
          onChange={setProjectId}
          disabled={submitting}
          testId="timer-project-picker"
        />
      </form>
      {error && (
        <div
          className="mt-3 text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700"
          data-testid="clock-error"
        >
          {error}
        </div>
      )}
    </div>
  );
}
